import React, { useEffect } from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

import { MOBILE_MENU_WIDGETS_WIDTH } from '../components/layout/constants';

/**
 * Width assumed while rendering on the server, where there is no window to measure. Reading
 * `window.innerWidth` during render threw on the server, which is why every consumer of this hook
 * had to be pulled in through a `dynamic(..., { ssr: false })` import. That kept the markup those
 * components produce out of the server rendered HTML entirely, so crawlers that do not execute
 * JavaScript never saw it. A desktop default is used so the server emits the desktop branch.
 */
const SERVER_RENDER_WIDTH = 1440;

const getWindowWidth = () => (typeof window === 'undefined' ? SERVER_RENDER_WIDTH : window.innerWidth);

export const useIsMobileView = () => {
  // The first client render must match the server render or hydration fails, so the real width is
  // only measured once mounted. `useIsomorphicLayoutEffect` applies it before the browser paints,
  // which keeps mobile visitors from seeing a frame of the desktop layout.
  const [windowWidth, setWindowWidth] = React.useState(SERVER_RENDER_WIDTH);
  const [isMobileView, setIsMobileView] = React.useState(SERVER_RENDER_WIDTH < MOBILE_MENU_WIDGETS_WIDTH);

  useIsomorphicLayoutEffect(() => {
    setWindowWidth(getWindowWidth());
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsMobileView(windowWidth < MOBILE_MENU_WIDGETS_WIDTH);
  }, [windowWidth]);

  return { isMobileView, windowWidth };
};
